import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { AttributeTypeFacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import TextField from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
});

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
    clearAllProductFilters,
};

export interface SupplyLandDimensionFilterStyles {
    filterContainer?: GridContainerProps;
    searchLabel?: InjectableCss;
    searchLabelContainer?: InjectableCss;
    searchInputContainer?: InjectableCss;
    searchButton?: ButtonPresentationProps;
    clearButton?: ButtonPresentationProps;
}

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const supplyLandDimensionFilterStyles: SupplyLandDimensionFilterStyles = {
    filterContainer: {
        css: css`
            align-self: flex-start;
            top: 10px;
            flex-basis: 250px;
            padding: 10px;
            button {
                width: 100%;
                border-radius: 10px;
                color: black;
            }
            label {
                text-align: center;
            }
            p {
                width: 100%;
                font-weight: bold;
                text-align: center;
                margin-bottom: 0;
            }
            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            input[type="number"] {
                -moz-appearance: textfield;
            }
        `,
        gap: 20,
    },
    searchLabel: {
        css: css`
            width: 20%;
            text-align: center;
        `,
    },
    searchLabelContainer: {
        css: css`
            padding: 0 20px;
        `,
    },
    searchInputContainer: {
        css: css`
            display: flex;
            width: 100%;
        `,
    },
    searchButton: {
        variant: "primary",
    },
    clearButton: {
        variant: "primary",
    },
};

const styles = supplyLandDimensionFilterStyles;

const SupplyLandDimensionssFilters: FC<Props> = ({
    attributeTypeFacets,
    addProductFilters,
    removeProductFilters,
    clearAllProductFilters,
}) => {
    if (!attributeTypeFacets?.length) {
        return null;
    }

    const lengthWidthHeightAttributes = attributeTypeFacets.filter(
        a => a.name === "Width (in.)" || a.name === "Length (in.)" || a.name === "Height (in.)",
    );

    const[allAttributeValueIds, setAllAttributeValueIds] = useState<AttributeTypeFacetModel[]>([]);
    useEffect(() => {
        setAllAttributeValueIds(lengthWidthHeightAttributes)
    }, []);

    const dimensionsSearchFormSubmit = (e: any) => {
        clearAllProductFilters({ removeAll: true });
        e.preventDefault();
        const length = parseFloat(
            (document.getElementById("searchLength") as HTMLInputElement).value === ""
                ? "0"
                : (document.getElementById("searchLength") as HTMLInputElement).value,
        );
        const width = parseFloat(
            (document.getElementById("searchWidth") as HTMLInputElement).value === ""
                ? "0"
                : (document.getElementById("searchWidth") as HTMLInputElement).value,
        );
        const height = parseFloat(
            (document.getElementById("searchHeight") as HTMLInputElement).value === ""
                ? "0"
                : (document.getElementById("searchHeight") as HTMLInputElement).value,
        );

        const lengthIds = findMatchingAttributeValue(length, "Length (in.)");
        const widthIds = findMatchingAttributeValue(width, "Width (in.)");
        const heightIds = findMatchingAttributeValue(height, "Height (in.)");
        const allIds = lengthIds?.concat(widthIds ?? []).concat(heightIds ?? []);
        addProductFilters({ attributeValueIds: allIds });
    };

    const findMatchingAttributeValue = (input: number, type: string) => {
        if (input !== 0) {
            const attribute = allAttributeValueIds.find(a => a.name === type);
            if (attribute) {
                const values = attribute.attributeValueFacets?.filter(
                    v =>
                        parseFloat(v.value) === input ||
                        parseFloat(v.value) === input - 1 ||
                        parseFloat(v.value) === input + 1,
                );
                if (values) {
                    return values.map(v => v.attributeValueId);
                }
            }
        }
    };

    const dimensionsSearchFormClear = () => {
        document.getElementById("searchLength")?.innerHTML === "";
        document.getElementById("searchWidth")?.innerHTML === "";
        document.getElementById("searchHeight")?.innerHTML === "";
        clearAllProductFilters({ removeAll: true });
    };

    const applyLengthRangeFilter = (min: number, max: number) => {
        clearAllProductFilters({ removeAll: true });
        const attribute = allAttributeValueIds.find(a => a.name === "Length (in.)");
        if (attribute) {
            const values = attribute.attributeValueFacets?.filter(
                v => parseFloat(v.value) >= min && parseFloat(v.value) <= max,
            );
            if (values) {
                addProductFilters({ attributeValueIds: values.map(v => v.attributeValueId) });
            }
        }
    };

    return (
        <>
            {lengthWidthHeightAttributes.length === 3 && (
                <GridContainer {...styles.filterContainer}>
                    <GridItem width={12}>
                        <Typography variant="p">Search by Size</Typography>
                    </GridItem>
                    <GridItem width={12} {...styles.searchLabelContainer}>
                        <StyledWrapper {...styles.searchLabel}>
                            <Typography weight={700}>L</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.searchLabel}>
                            <Typography weight={700}>x</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.searchLabel}>
                            <Typography weight={700}>W</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.searchLabel}>
                            <Typography weight={700}>x</Typography>
                        </StyledWrapper>
                        <StyledWrapper {...styles.searchLabel}>
                            <Typography weight={700}>H</Typography>
                        </StyledWrapper>
                    </GridItem>

                    <form className="searchForm">
                        <StyledWrapper {...styles.searchInputContainer}>
                            <GridItem width={4}>
                                <TextField type="number" max={100} min={0} id="searchLength" />
                            </GridItem>
                            <GridItem width={4}>
                                <TextField type="number" max={100} min={0} id="searchWidth" />
                            </GridItem>
                            <GridItem width={4}>
                                <TextField type="number" max={100} min={0} id="searchHeight" />
                            </GridItem>
                        </StyledWrapper>

                        <GridItem width={12}>
                            <Button {...styles.searchButton} type="submit" onClick={e => dimensionsSearchFormSubmit(e)}>
                                Search
                            </Button>
                        </GridItem>

                        <GridItem width={12}>
                            <Button {...styles.clearButton} type="reset" onClick={() => dimensionsSearchFormClear()}>
                                Clear
                            </Button>
                        </GridItem>
                    </form>
                    <GridItem width={12}>
                        <Typography variant="p">Filter by Size Range</Typography>
                    </GridItem>
                    <GridItem width={12}>
                        <Button variant="primary" onClick={e => applyLengthRangeFilter(3, 8)}>
                            3" - 8"
                        </Button>
                    </GridItem>
                    <GridItem width={12}>
                        <Button variant="primary" onClick={e => applyLengthRangeFilter(9, 11)}>
                            9" - 11"
                        </Button>
                    </GridItem>
                    <GridItem width={12}>
                        <Button variant="primary" onClick={e => applyLengthRangeFilter(12, 13)}>
                            12" - 13"
                        </Button>
                    </GridItem>
                    <GridItem width={12}>
                        <Button variant="primary" onClick={e => applyLengthRangeFilter(14, 15)}>
                            14" - 15"
                        </Button>
                    </GridItem>
                    <GridItem width={12}>
                        <Button variant="primary" onClick={e => applyLengthRangeFilter(16, 17)}>
                            16" - 17"
                        </Button>
                    </GridItem>
                    <GridItem width={12}>
                        <Button variant="primary" onClick={e => applyLengthRangeFilter(18, 19)}>
                            18" - 19"
                        </Button>
                    </GridItem>
                    <GridItem width={12}>
                        <Button variant="primary" onClick={e => applyLengthRangeFilter(20, 23)}>
                            20" - 23"
                        </Button>
                    </GridItem>
                    <GridItem width={12}>
                        <Button variant="primary" onClick={e => applyLengthRangeFilter(24, 48)}>
                            24" - 48"
                        </Button>
                    </GridItem>
                </GridContainer>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(SupplyLandDimensionssFilters),
    definition: {
        group: "Product List",
        displayName: "SupplyLand Dimensions Filters",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
